import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { useTextResources } from "../../language";
import HotelsDropDown from "../shared/HotelsDropDown";
import TextInput from "../shared/TextInput";
import WingDropDown from "../shared/WingDropDown";
import PropTypes from "prop-types";
import { onWarning } from "../shared/messages";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import { GetWings } from "../../api/services/organization";
import { AllocationAndStrategy } from "../../api/services/next";
import { onSuccess } from '../../components/shared/messages';
import { useLoader } from "../../hooks/useLoader";

export const StrategyCreationPopup = ({ extendedProps: { addRow } = {}, open, onCancelChanges, onApplyChanges }) => {
  const currentHotel = useCurrentHotel();
  const text = useTextResources();
  const [strategy, setStrategy] = useState({});
  const [wings, setWings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const loader = useLoader();

  useEffect(() => {
    setStrategy({});
  }, [open])

  const changeStrategyField = useCallback((fieldName, value) => {
    setStrategy((prevState) => ({ ...prevState, [fieldName]: value }));
  }, []);

  const setName = useCallback((value) => changeStrategyField("Name", value), []);
  const setHotel = useCallback((value) => changeStrategyField("HotelId", value), []);
  const setWing = useCallback((value) => changeStrategyField("WingId", value), []);

  useEffect(() => {
    if (strategy.HotelId)
      GetWings({ HotelId: strategy.HotelId }).then(res => {
        if (res.length === 1) {
          changeStrategyField("WingId", res[0].Id);
        }
        setWings(res);
      })
  }, [strategy.HotelId]);

  const hotelsSpec = useMemo(() => ({ HotelChainId: currentHotel?.HotelChainId }), [currentHotel]);
  const wingSpec = useMemo(() => ({ HotelId: strategy.HotelId }), [strategy.HotelId]);

  const getMissingFields = useCallback((strategyObj) => {
    let missingFields = [];
    if (!strategyObj.Name)
      missingFields.push(text("Name"));
    if (!strategyObj.HotelId)
      missingFields.push(text("Hotel"));
    if (!strategyObj.WingId)
      missingFields.push(text("Wing"));
    return missingFields;
  }, [text]);

  const onSave = useCallback(async () => {
    const missingFields = getMissingFields(strategy);
    if (missingFields.length === 0) {
      setIsLoading(true);
      try {
        loader(true)
        await AllocationAndStrategy.AddStrategy(strategy);
        onSuccess();
        await addRow();
        onApplyChanges && onApplyChanges();
      } catch (error) {
        onWarning(error?.error?.detail);
      } finally {
        loader(false);
      }
      setIsLoading(false);
    } else {
      onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
    }
  }, [strategy, getMissingFields, onApplyChanges, text]);

  return (
    <Modal size="small" open={open} onClose={onCancelChanges}>
      <Modal.Header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {`${text("Create")} ${text("Strategy")}`} <Button circular icon="close" onClick={onCancelChanges} />
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field
            variant="outlined"
            control={TextInput}
            label={text("Name")}
            onChange={setName}
            value={strategy.Name}
          />
          <Form.Field
            fluid
            variant="outlined"
            label={text("Hotel")}
            required
            control={HotelsDropDown}
            onChange={setHotel}
            spec={hotelsSpec}
            value={strategy.HotelId}
            search
          />
          <Form.Field
            fluid
            variant="outlined"
            label={text("Wing")}
            required
            control={WingDropDown}
            onChange={setWing}
            spec={wingSpec}
            data={wings}
            value={strategy.WingId}
            search
          />
        </Form>
      </Modal.Content>
      <Modal.Actions style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button positive onClick={onSave} loading={isLoading}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

StrategyCreationPopup.propTypes = {
  extendedProps: PropTypes.object,
  open: PropTypes.bool,
  onCancelChanges: PropTypes.func,
  onApplyChanges: PropTypes.func,
};
